import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { router, Link } from '@inertiajs/react'
import OperatorLayout from '@/layouts/OperatorLayout'
import { HelpTooltip } from '@/components/HelpTooltip'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Target,
  ChevronsDown,
  X,
  Rocket,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Minus,
  ChevronRight,
  CornerDownRight,
  Info,
  MoreHorizontal,
  ArrowRight,
} from 'lucide-react'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { cn } from '@/lib/utils'
import { currencySymbol } from '@/lib/calendar-utils'
import { formatMoneySym } from '@/lib/format'
import {
  velocityDescriptor,
  gapPhrase,
  gapTone,
  GAP_TONE_CLASS,
  bandPhrase,
  confidenceBand,
  engineNarration,
  entryGuidance,
  type ForecastContext,
} from '@/lib/target-utils'
import { apiFetch } from '@/lib/csrf'
import UnitScopeNote from '@/components/groups/UnitScopeNote'
import DeferredScopeNote from '@/components/groups/DeferredScopeNote'
import { AcknowledgedChip, KeepTargetButton } from '@/components/targets/VerdictAck'
import { TargetResultsSection } from '@/components/targets/TargetResultsSection'
import type { AdrScope, SharedProps, TargetMonthResult, TargetVerdict } from '@/types/shared'

// ─── Types ────────────────────────────────────────────────────────────────────

interface SavedMonthTarget {
  month: number
  occupancy: number | null
  adr: number | null
  revpar: number | null
  weight: number | null
  note: string | null
}

interface LastYearActual {
  occupancy: number
  adr: number | null
  revpar: number
  /** Room-nights sold that month last year — the sample the figures rest on. */
  nights: number
  /** True when the property barely traded, so the month is not a benchmark. */
  sparse: boolean
}

interface PreviewSampleRow {
  date: string
  before_amount: number
  after_amount: number
  modifier_pct: number
  room_type_name: string
  rate_plan_name: string
  // D19 — present only on linked-room rows derived from their parent's rate
  derived?: boolean
  // Combined inventory: a Same room's row, which takes its twin's (the parent fields) figure
  same_room?: boolean
  parent_room_type_id?: number
  parent_room_type_name?: string
}

interface PreviewByMonth {
  [month: number]: {
    dates: string[]
    avg_pct: number
  }
}

interface PreviewResult {
  dates_affected: number
  avg_modifier_pct: number
  sample: PreviewSampleRow[]
  by_month: PreviewByMonth
  /**
   * T4.2 — one entry per drafted month whose forecast carries a band. `position` is classified
   * server-side; the row maps it to a sentence and never compares the numbers itself.
   */
  forecast_context: Record<number, ForecastContext>
}

interface DraftRow {
  occupancy: string
  adr: string
  /**
   * Carried, never edited. RevPAR targets are stored but no engine stage reads one (the enum
   * value in `monthly_target.rb` is its only backend appearance), so the column was removed —
   * but the save payload still round-trips whatever is on the record so removing the input
   * cannot silently delete a value.
   */
  revpar: string
  isDirty: boolean
  willClear: boolean
}

// Per-month forecast surface (D10)
interface MonthForecast {
  targetYear: number
  daysRemaining: number
  state: string | null
  forecastEom: number | null
  forecastP10: number | null
  forecastP90: number | null
  targetEom: number | null
  targetGap: number | null
  monthConfidence: number | null
  capTrimmedPct: number | null
  atFloorPct: number | null
  adrFloorAmount: number | null
  dormantReason: string | null
  computedAt: string | null
  // D12 (C6) — booking velocity for the row chip; nil on pre-v3 snapshots.
  velocityRatio: number | null
  velocityRelevance: number | null
  pending: boolean
  capTrimmed: boolean
}

interface TargetsProps extends SharedProps {
  savedTargets: SavedMonthTarget[]
  lastYearActuals: Record<number, LastYearActual | null>
  /** One per saved target month — the same server-side resolution the dashboard renders. */
  verdicts: TargetVerdict[]
  /**
   * One per CLOSED occurrence of a target month this calendar year. Empty for most properties
   * until a month actually ends, and the section renders nothing at all in that case.
   */
  results?: TargetMonthResult[]
  forecastByMonth?: Record<number, MonthForecast>
  /** Combined inventory: ADR is the building's, and the floor is in the lead listing's prices. */
  adrScope?: AdrScope | null
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

const MONTH_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

const ALL_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseDraftRow(saved: SavedMonthTarget | undefined): DraftRow {
  return {
    occupancy: saved?.occupancy != null ? String(saved.occupancy) : '',
    adr: saved?.adr != null ? String(saved.adr) : '',
    revpar: saved?.revpar != null ? String(saved.revpar) : '',
    isDirty: false,
    willClear: false,
  }
}

/**
 * Read a verdict CTA's `?suggest=<month>:<occupancy>` seed. Returns null for anything malformed
 * or out of range — a bad query string must never put a nonsense number into a draft the
 * operator might then deploy.
 */
function parseSuggestion(search: string): { month: number; occupancy: number } | null {
  const raw = new URLSearchParams(search).get('suggest')
  if (!raw) return null

  const [monthPart, occPart] = raw.split(':')
  const month = Number(monthPart)
  const occupancy = Number(occPart)
  if (!Number.isInteger(month) || month < 1 || month > 12) return null
  if (!Number.isFinite(occupancy) || occupancy <= 0 || occupancy > 100) return null

  return { month, occupancy }
}

function hasSavedValues(saved: SavedMonthTarget | undefined): boolean {
  if (!saved) return false
  return saved.occupancy != null || saved.adr != null || saved.revpar != null
}

// ─── Plain-language readouts ──────────────────────────────────────────────────
//
// `gapPhrase` / `bandPhrase` / `confidenceBand` / `engineNarration` live in `lib/target-utils`
// so they can be tested without mounting the page.

const FORECAST_STATE_CONFIG: Record<string, { label: string; badgeClass: string }> = {
  on_track: { label: 'On track',  badgeClass: 'bg-emerald-100 text-emerald-700' },
  pushing:  { label: 'Pushing',   badgeClass: 'bg-rose-100 text-rose-700' },
  easing:   { label: 'Easing',    badgeClass: 'bg-emerald-100 text-emerald-700' },
  at_floor: { label: 'At floor',  badgeClass: 'bg-amber-100 text-amber-700' },
  dormant:  { label: 'Dormant',   badgeClass: 'bg-slate-100 text-slate-600' },
}

function relativeTime(iso: string | null): string {
  if (!iso) return 'not yet'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 2) return 'just now'
  if (mins < 60) return `${mins} min ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

/**
 * Number inputs change their value on a stray scroll wheel while focused. On a page of target
 * commitments that is a silent edit, so every numeric field drops focus on wheel.
 */
function blurOnWheel(e: React.WheelEvent<HTMLInputElement>) {
  e.currentTarget.blur()
}

/**
 * Acknowledging (or un-acknowledging) a verdict changes only the verdict payload — the targets,
 * the forecast and last year's actuals are all untouched, so a partial reload is the honest
 * refresh here.
 */
function reloadVerdicts() {
  router.reload({ only: ['verdicts'] })
}

// ─── Row ──────────────────────────────────────────────────────────────────────

interface TargetRowProps {
  month: number
  /** The property's backend-served canonical path. */
  base: string
  saved: SavedMonthTarget | undefined
  actual: LastYearActual | null
  draft: DraftRow
  currency: string
  isCurrentMonth: boolean
  forecast: MonthForecast | undefined
  verdict: TargetVerdict | undefined
  /**
   * Where this row's DRAFT target lands against the month's forecast band. Only passed while the
   * row is being edited — an untouched month already reads its band in the projection column, and
   * repeating it on every row would be noise.
   */
  forecastContext: ForecastContext | undefined
  onUpdate: (month: number, field: 'occupancy' | 'adr', value: string) => void
  onApplyRemaining: (month: number) => void
  onClearRow: (month: number) => void
  onApplySuggestion: (value: number, month: number) => void
  animDelay: number
}

function TargetRow({
  month,
  base,
  saved,
  actual,
  draft,
  currency,
  isCurrentMonth,
  forecast,
  verdict,
  forecastContext,
  onUpdate,
  onApplyRemaining,
  onClearRow,
  onApplySuggestion,
  animDelay,
}: TargetRowProps) {
  const sym = currencySymbol(currency)
  const savedForMonth = hasSavedValues(saved)
  const hasDraftValues = !!(draft.occupancy || draft.adr)
  const rowHasChanges = draft.isDirty || draft.willClear

  // ADR alone does NOT opt a property into Target Pressure — only a saved occupancy target
  // activates the engine. Surface it the moment a row carries a floor but no target.
  const floorWithoutTarget = !draft.occupancy && !!draft.adr

  const velocity = forecast ? velocityDescriptor(forecast.velocityRatio, forecast.velocityRelevance) : null
  const stateCfg = forecast?.state ? FORECAST_STATE_CONFIG[forecast.state] : null
  const confidence = forecast ? confidenceBand(forecast.monthConfidence) : null
  const band = forecast ? bandPhrase(forecast) : null
  const guidance = entryGuidance(forecastContext)

  const showVerdict = !!verdict && (verdict.severity === 'attention' || verdict.severity === 'watch')
  // The block is gone once a month is acknowledged, so the record of that decision — and its
  // undo — has to live on the row itself, beside the month's other status.
  const acknowledged = verdict?.quietReason === 'acknowledged'

  return (
    <>
      <TableRow
        className="animate-slide-in group border-b-0 align-top hover:bg-muted/20"
        style={{ animationDelay: `${animDelay}ms` }}
      >
        {/* Month — opens this month's full breakdown */}
        <TableCell className="py-3.5 pl-5 pr-3 align-top">
          <div className="flex items-start gap-2.5">
            <div
              className={cn(
                'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-[11px] font-bold',
                isCurrentMonth ? 'bg-primary text-white' : 'bg-muted text-muted-foreground',
              )}
            >
              {MONTH_SHORT[month - 1]}
            </div>
            <div className="min-w-0">
              <Link
                href={`${base}/targets/${month}`}
                className="group/link inline-flex items-center gap-1 text-sm font-medium leading-none text-foreground hover:text-primary"
              >
                {MONTH_NAMES[month - 1]}
                <ArrowRight className="h-3 w-3 opacity-0 transition-opacity group-hover/link:opacity-100" />
              </Link>
              <div className="mt-1.5 flex flex-wrap items-center gap-1">
                {isCurrentMonth && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-primary">This month</span>
                )}
                {rowHasChanges ? (
                  <Badge className="border-0 bg-amber-100 text-[10px] text-amber-700">Draft</Badge>
                ) : savedForMonth ? (
                  <Badge className="border-0 bg-emerald-100 text-[10px] text-emerald-700">Saved</Badge>
                ) : null}
                {acknowledged && (
                  <AcknowledgedChip
                    propertyBasePath={base}
                    month={month}
                    monthName={MONTH_NAMES[month - 1]}
                    onDone={reloadVerdicts}
                  />
                )}
              </div>
              {draft.willClear && <p className="mt-1 text-[11px] text-rose-500">Will clear on save</p>}
            </div>
          </div>
        </TableCell>

        {/* Target */}
        <TableCell className="px-3 py-3.5 align-top">
          <div className="relative w-24">
            <Input
              type="number"
              min="0"
              max="100"
              step="1"
              value={draft.occupancy}
              placeholder="—"
              onWheel={blurOnWheel}
              onChange={(e) => onUpdate(month, 'occupancy', e.target.value)}
              className="h-8 pr-7 text-sm font-semibold tabular-nums"
              aria-label={`${MONTH_NAMES[month - 1]} occupancy target`}
            />
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
              %
            </span>
          </div>
          {/* Guidance while they type, not a verdict two hours after they commit. Never a block:
              the number stays whatever the operator typed. Backend-classified — this renders a
              sentence and two numbers, it compares nothing. */}
          {guidance && <p className={cn('mt-1 text-[11px] leading-snug', guidance.className)}>{guidance.text}</p>}
          {actual && !actual.sparse && (
            <p className="mt-1 text-[11px] text-muted-foreground">Last year: {actual.occupancy}%</p>
          )}
          {actual?.sparse && (
            <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground/70">
              No useful last year
              <HelpTooltip
                content={`${MONTH_NAMES[month - 1]} last year sold only ${actual.nights} room-night${
                  actual.nights === 1 ? '' : 's'
                } (${actual.occupancy}% occupancy), so it isn’t a fair comparison for this year’s target.`}
              />
            </p>
          )}
          {floorWithoutTarget && (
            <p className="mt-1 flex items-start gap-1 text-[11px] text-amber-600">
              <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
              <span>A floor on its own doesn’t steer rates — set a target to switch this month on.</span>
            </p>
          )}
        </TableCell>

        {/* ADR floor */}
        <TableCell className="px-3 py-3.5 align-top">
          <div className="relative w-24">
            <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">
              {sym}
            </span>
            <Input
              type="number"
              min="0"
              step="1"
              value={draft.adr}
              placeholder={forecast?.adrFloorAmount != null ? forecast.adrFloorAmount.toFixed(0) : '—'}
              onWheel={blurOnWheel}
              onChange={(e) => onUpdate(month, 'adr', e.target.value)}
              className="h-8 pl-6 text-sm tabular-nums"
              aria-label={`${MONTH_NAMES[month - 1]} ADR floor`}
            />
          </div>
          {!draft.adr && forecast?.adrFloorAmount != null && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              Using {formatMoneySym(forecast.adrFloorAmount, sym)}
            </p>
          )}
        </TableCell>

        {/* Projected end-of-month + forecast band */}
        <TableCell className="px-3 py-3.5 align-top">
          {forecast?.forecastEom != null ? (
            <>
              <span
                className={cn(
                  'text-sm font-semibold tabular-nums',
                  forecast.state === 'dormant' ? 'text-muted-foreground' : 'text-foreground',
                )}
              >
                {forecast.forecastEom.toFixed(1)}%
              </span>
              {band && <p className="mt-1 text-[11px] text-muted-foreground tabular-nums">{band}</p>}
            </>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </TableCell>

        {/* Target gap */}
        <TableCell className="px-3 py-3.5 align-top">
          {forecast?.targetGap != null ? (
            <>
              {/* Tone follows the engine's own ruling: an `on_track` month's gap is inside the
                  noise floor, so it reads neutrally rather than as a rose shortfall beside an
                  emerald "On track" badge. */}
              <span
                className={cn(
                  'text-sm font-semibold tabular-nums',
                  GAP_TONE_CLASS[gapTone(forecast.targetGap, forecast.state)],
                )}
              >
                {gapPhrase(forecast.targetGap)}
              </span>
              {forecast.daysRemaining != null && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  {forecast.daysRemaining === 0
                    ? 'Month is done'
                    : `${forecast.daysRemaining} day${forecast.daysRemaining === 1 ? '' : 's'} left`}
                </p>
              )}
            </>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </TableCell>

        {/* What the engine is doing */}
        <TableCell className="px-3 py-3.5 align-top">
          {forecast ? (
            forecast.pending ? (
              <div className="flex items-center gap-1.5 text-xs text-amber-600">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
                Recalculating — these figures still show the previous target.
              </div>
            ) : (
              <div className="space-y-1.5">
                <div className="flex flex-wrap items-center gap-1">
                  {stateCfg && (
                    <Badge className={cn('border-0 text-[10px]', stateCfg.badgeClass)}>{stateCfg.label}</Badge>
                  )}
                  {confidence && (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span
                            className={cn(
                              'inline-flex cursor-default items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold',
                              confidence.className,
                            )}
                          >
                            {confidence.label}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-[240px]">
                          Projection confidence: {Math.round((forecast.monthConfidence ?? 0) * 100)}% — how much the
                          engine trusts this month’s projection. It builds as the property books more history.
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  {forecast.capTrimmed && (
                    <Badge className="border-0 bg-slate-100 text-[10px] text-slate-600">Trimmed by demand</Badge>
                  )}
                </div>
                <p className="text-[11px] leading-snug text-muted-foreground">{engineNarration(forecast)}</p>
                {velocity && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <p className="w-fit cursor-default text-[11px] text-muted-foreground">
                          Bookings arriving <span className="font-medium text-foreground">{velocity.display}</span>
                        </p>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-[260px]">
                        {velocity.tooltip}
                        {velocity.relevanceNote ? ` ${velocity.relevanceNote}` : ''}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
                <p className="text-[10px] text-muted-foreground/70">Updated {relativeTime(forecast.computedAt)}</p>
              </div>
            )
          ) : savedForMonth || hasDraftValues ? (
            <p className="text-[11px] text-muted-foreground">Not calculated yet.</p>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </TableCell>

        {/* Row actions */}
        <TableCell className="py-3.5 pl-2 pr-4 text-right align-top">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-muted-foreground"
                aria-label={`Actions for ${MONTH_NAMES[month - 1]}`}
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-60 p-1">
              <Link
                href={`${base}/targets/${month}`}
                className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-xs text-foreground hover:bg-muted"
              >
                <TrendingUp className="h-3.5 w-3.5 text-muted-foreground" />
                Open {MONTH_NAMES[month - 1]} breakdown
              </Link>
              {hasDraftValues && (
                <button
                  type="button"
                  onClick={() => onApplyRemaining(month)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-foreground hover:bg-muted"
                >
                  <ChevronsDown className="h-3.5 w-3.5 text-muted-foreground" />
                  Copy to every later month
                </button>
              )}
              {(hasDraftValues || savedForMonth) && (
                <button
                  type="button"
                  onClick={() => onClearRow(month)}
                  className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-rose-500 hover:bg-rose-50"
                >
                  <X className="h-3.5 w-3.5" />
                  {savedForMonth ? 'Stop steering this month' : 'Clear'}
                </button>
              )}
            </PopoverContent>
          </Popover>
        </TableCell>
      </TableRow>

      {/* The verdict sits under the month it describes. One per month — the precedence ladder in
          ResolveTargetVerdictMutation already picked the winner, and everything it beat is inside
          the disclosure. */}
      {showVerdict && verdict && (
        <TableRow className="border-b border-border/50 hover:bg-transparent">
          <TableCell colSpan={7} className="px-5 pb-4 pt-0">
            <div
              className={cn(
                'flex gap-3 rounded-lg border px-3.5 py-3 text-sm',
                verdict.severity === 'attention'
                  ? 'border-amber-200 bg-amber-50 text-amber-900'
                  : 'border-border/60 bg-muted/40 text-foreground',
              )}
            >
              <AlertTriangle
                className={cn(
                  'mt-0.5 h-4 w-4 shrink-0',
                  verdict.severity === 'attention' ? 'text-amber-500' : 'text-muted-foreground/60',
                )}
              />
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-semibold leading-snug">{verdict.headline}</p>
                <p className="mt-0.5 text-xs leading-relaxed opacity-80">{verdict.detail}</p>
                {/* The evidence behind the claim, in a footnote's voice: one step down the type
                    scale and a shade quieter than the detail. Backend-composed — rendered
                    verbatim, never assembled here. */}
                {verdict.credibility && (
                  <p className="mt-1.5 text-[11px] leading-relaxed opacity-60">{verdict.credibility}</p>
                )}
                {verdict.supporting.length > 0 && (
                  <details className="group mt-2">
                    <summary className="inline-flex cursor-pointer list-none items-center gap-1 rounded text-[11px] font-medium opacity-70 outline-none transition-opacity hover:opacity-100 focus-visible:ring-2 focus-visible:ring-primary/40">
                      <ChevronRight className="h-3 w-3 transition-transform group-open:rotate-90" />
                      What else the engine noticed ({verdict.supporting.length})
                    </summary>
                    <ul className="mt-2 space-y-2 border-l border-border/50 pl-3">
                      {verdict.supporting.map((s) => (
                        <li key={s.id}>
                          <p className="text-[11px] font-medium">{s.title}</p>
                          <p className="mt-0.5 text-[11px] leading-relaxed opacity-75">{s.body}</p>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
              {/* Primary above, quieter below: take the number, or keep the one you chose. */}
              <div className="flex shrink-0 flex-col items-end justify-center gap-1">
                {verdict.recommendation && (
                  <button
                    type="button"
                    onClick={() => onApplySuggestion(verdict.recommendation!.value, verdict.targetMonth)}
                    className="rounded-md bg-amber-100 px-3 py-1.5 text-xs font-medium text-amber-900 transition-colors hover:bg-amber-200"
                  >
                    {verdict.recommendation.label}
                  </button>
                )}
                <KeepTargetButton
                  propertyBasePath={base}
                  month={verdict.targetMonth}
                  monthName={verdict.monthName}
                  tone={verdict.severity === 'attention' ? 'amber' : 'muted'}
                  onDone={reloadVerdicts}
                  className="items-end"
                />
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  )
}

// ─── Impact preview ───────────────────────────────────────────────────────────
//
// Only ever rendered when a draft exists. It used to occupy a permanent 420px rail beside the
// table — 29% of the viewport holding a placeholder, which squeezed the table to 860px against
// its 1323px of content and clipped five forecast columns into an `overflow-hidden` box with no
// scrollbar. The preview now lives in a bar that appears when it has something to say.

interface PreviewBodyProps {
  preview: PreviewResult
  currency: string
}

function PreviewBody({ preview, currency }: PreviewBodyProps) {
  const sym = currencySymbol(currency)
  const { sample, by_month } = preview

  const byMonthRows = Object.entries(by_month)
    .map(([m, v]) => ({ month: parseInt(m, 10), datesCount: v.dates.length, avgPct: v.avg_pct }))
    .sort((a, b) => a.month - b.month)

  const maxAbsPct = Math.max(...byMonthRows.map((r) => Math.abs(r.avgPct)), 0.1)
  // A single-month breakdown is the headline figure again — only worth its own column when
  // there are several months to compare across.
  const showByMonth = byMonthRows.length > 1

  return (
    <div className={cn('grid gap-6', showByMonth && sample.length > 0 && 'md:grid-cols-2')}>
      {showByMonth && (
        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">By month</p>
            <HelpTooltip content="The same signal applies to every date in the window, but its effect is gentler for dates further out." />
          </div>
          <div className="space-y-2">
            {byMonthRows.map((r) => {
              const rowIsUp = r.avgPct > 0
              const widthPct = Math.max(8, Math.round((Math.abs(r.avgPct) / maxAbsPct) * 100))
              return (
                <div key={r.month} className="flex items-center gap-2 text-xs">
                  <span className="w-8 shrink-0 font-medium text-foreground">{MONTH_SHORT[r.month - 1]}</span>
                  <span className="w-16 shrink-0 text-[11px] tabular-nums text-muted-foreground">
                    {r.datesCount} dates
                  </span>
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-muted">
                    <div
                      className={cn('h-full rounded-full', rowIsUp ? 'bg-emerald-400' : 'bg-rose-400')}
                      style={{ width: `${widthPct}%` }}
                    />
                  </div>
                  <span
                    className={cn(
                      'w-12 shrink-0 text-right text-[11px] font-semibold tabular-nums',
                      rowIsUp ? 'text-emerald-700' : 'text-rose-600',
                    )}
                  >
                    {rowIsUp ? '+' : ''}
                    {r.avgPct}%
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {sample.length > 0 && (
        <div>
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Sample changes
          </p>
          <div className="divide-y divide-border/40">
            {sample.slice(0, 5).map((row, i) => {
              const pct = row.modifier_pct
              const isUp = pct > 0
              // D19 — linked-child rows inherit their move via the parent (the parent runs the
              // stage; children anchor to it). Distinct, indented treatment so the operator reads
              // "this isn't an independent decision" at a glance.
              const isDerived = row.derived === true && !!row.parent_room_type_name
              return (
                <div
                  key={i}
                  className={cn('space-y-1 py-2 first:pt-0 last:pb-0', isDerived && 'ml-0.5 border-l-2 border-primary/30 pl-3')}
                >
                  <div className="flex items-center gap-1.5">
                    <p className="text-[11px] font-semibold tabular-nums text-foreground">{row.date}</p>
                    <p className="truncate text-[11px] text-muted-foreground">{row.room_type_name}</p>
                    {isDerived && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide text-primary">
                        <CornerDownRight className="h-2.5 w-2.5" />
                        {row.same_room ? `Same room as ${row.parent_room_type_name}` : `via ${row.parent_room_type_name}`}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 text-xs">
                    <span className="tabular-nums text-muted-foreground">{formatMoneySym(row.before_amount, sym)}</span>
                    <ChevronRight className="h-3 w-3 text-muted-foreground/40" />
                    <span className={cn('font-semibold tabular-nums', isUp ? 'text-emerald-700' : 'text-rose-600')}>
                      {formatMoneySym(row.after_amount, sym)}
                    </span>
                    <span
                      className={cn(
                        'ml-auto rounded px-1.5 py-0.5 text-[10px] font-semibold',
                        isUp ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-600',
                      )}
                    >
                      {isUp ? '+' : ''}
                      {pct}%
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      <p className="col-span-full flex items-start gap-1.5 text-[11px] leading-relaxed text-muted-foreground">
        <Info className="mt-0.5 h-3 w-3 shrink-0" />
        This compares each drafted month’s target against the engine’s current projection. Real rates update on the
        next refresh, about every hour.
      </p>
    </div>
  )
}

interface PreviewBarProps {
  preview: PreviewResult | null
  loading: boolean
  currency: string
  editedCount: number
  saving: boolean
  onDiscard: () => void
  onSave: () => void
}

function PreviewBar({ preview, loading, currency, editedCount, saving, onDiscard, onSave }: PreviewBarProps) {
  const [open, setOpen] = useState(false)
  const hasDetail = !!preview && preview.dates_affected > 0

  return (
    <div className="sticky bottom-0 z-20 -mx-4 mt-6 border-t border-border/60 bg-white/95 px-4 py-3 shadow-[0_-4px_16px_-8px_rgba(15,23,42,0.15)] backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
      {open && hasDetail && (
        <div className="mb-3 max-h-[40vh] overflow-y-auto rounded-lg border border-border/60 bg-muted/20 p-4">
          <PreviewBody preview={preview!} currency={currency} />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <div className="min-w-0 flex-1">
          {loading ? (
            <div className="space-y-1.5">
              <Skeleton className="h-3.5 w-40" />
              <Skeleton className="h-3 w-64" />
            </div>
          ) : preview && preview.dates_affected > 0 ? (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 text-sm font-semibold tabular-nums',
                  preview.avg_modifier_pct > 0 ? 'text-emerald-700' : 'text-rose-600',
                )}
              >
                {preview.avg_modifier_pct > 0 ? (
                  <TrendingUp className="h-4 w-4" />
                ) : (
                  <TrendingDown className="h-4 w-4" />
                )}
                {preview.avg_modifier_pct > 0 ? '+' : ''}
                {preview.avg_modifier_pct}% average rate change
              </span>
              <span className="text-xs text-muted-foreground">
                on {preview.dates_affected} dates over the next 60 days
              </span>
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                <ChevronRight className={cn('h-3 w-3 transition-transform', open && 'rotate-90')} />
                {open ? 'Hide the breakdown' : 'See the breakdown'}
              </button>
            </div>
          ) : preview ? (
            <div className="flex items-center gap-2">
              <Minus className="h-4 w-4 shrink-0 text-muted-foreground" />
              <p className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">No rate change yet.</span> The gap between these targets
                and the projection is too small to act on — the engine will hold until it widens.
              </p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">
              {editedCount} month{editedCount === 1 ? '' : 's'} edited.
            </p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <Button variant="ghost" size="sm" className="rounded-lg" onClick={onDiscard} disabled={saving}>
            Discard changes
          </Button>
          <Button size="sm" className="gap-1.5 rounded-lg" onClick={onSave} disabled={saving}>
            <Rocket className="h-3.5 w-3.5" />
            Save &amp; deploy
          </Button>
        </div>
      </div>
    </div>
  )
}

// ─── Empty state ───────────────────────────────────────────────────────────────

function EmptyStateCard() {
  return (
    <div className="animate-slide-in rounded-xl border border-dashed border-border bg-white p-8 shadow-sm">
      <div className="flex items-start gap-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10">
          <Target className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">You’re not steering any month yet</p>
          <p className="mt-1.5 max-w-lg text-sm leading-relaxed text-muted-foreground">
            Right now the engine prices every month from your booking history alone. Put an occupancy target on a
            month and it starts working towards it — discounting when you’re behind, holding out for more when
            you’re ahead.
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            Targets start steering within the hour, and you can change or remove them any time.{' '}
            <Link href="/guides/targets" className="text-primary hover:underline">
              How targets work →
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function Targets({
  currentUser,
  property,
  properties,
  savedTargets,
  lastYearActuals,
  verdicts,
  results = [],
  forecastByMonth = {},
  adrScope = null,
  reportingScope,
}: TargetsProps) {
  const base = property.basePath
  const currentMonth = new Date().getMonth() + 1

  // A verdict's "Set July target to 74%" CTA arrives as `?suggest=7:74`. It seeds the DRAFT and
  // nothing else — the operator still sees the impact preview and presses Save & deploy, exactly
  // as if they had typed the number. The system recommends; it never writes on the operator's
  // behalf, and an unrealistic target stays entirely their call.
  const suggestion = parseSuggestion(typeof window !== 'undefined' ? window.location.search : '')

  const savedByMonth = useMemo(
    () => Object.fromEntries(savedTargets.map((t) => [t.month, t])) as Record<number, SavedMonthTarget>,
    [savedTargets],
  )

  const buildInitialDraft = useCallback(() => {
    const init: Record<number, DraftRow> = {}
    for (let m = 1; m <= 12; m++) init[m] = parseDraftRow(savedByMonth[m])
    if (suggestion && init[suggestion.month]) {
      init[suggestion.month] = {
        ...init[suggestion.month],
        occupancy: String(suggestion.occupancy),
        isDirty: true,
        willClear: false,
      }
    }
    return init
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedByMonth])

  const [draft, setDraft] = useState<Record<number, DraftRow>>(buildInitialDraft)

  const [preview, setPreview] = useState<PreviewResult | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [saving, setSaving] = useState(false)

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const monthIsEdited = useCallback(
    (m: number): boolean => {
      const row = draft[m]
      const saved = savedByMonth[m]
      if (row.isDirty || row.willClear) return true
      const savedOcc = saved?.occupancy != null ? String(saved.occupancy) : ''
      const savedAdr = saved?.adr != null ? String(saved.adr) : ''
      return row.occupancy !== savedOcc || row.adr !== savedAdr
    },
    [draft, savedByMonth],
  )

  const editedMonths = ALL_MONTHS.filter(monthIsEdited)
  const hasDraftChanges = editedMonths.length > 0
  // The preview payload carries every month, but the band line belongs only to the rows the
  // operator is actually editing — it is feedback on a number being typed, not a permanent readout.
  const editedSet = new Set(editedMonths)
  const anyTargetsSaved = savedTargets.length > 0

  const verdictByMonth = useMemo(
    () => Object.fromEntries(verdicts.map((v) => [v.targetMonth, v])) as Record<number, TargetVerdict>,
    [verdicts],
  )

  const applySuggestion = (value: number, month: number) => {
    setDraft((prev) => ({
      ...prev,
      [month]: { ...prev[month], occupancy: String(value), isDirty: true, willClear: false },
    }))
  }

  // Build the preview payload (all 12 months with their current draft values). `revpar` is
  // round-tripped untouched — it has no input any more, and dropping it would delete the value.
  const buildPreviewPayload = useCallback(() => {
    return ALL_MONTHS.map((m) => {
      const row = draft[m]
      if (row.willClear) return { month: m, occupancy: null, adr: null, revpar: null }
      return {
        month: m,
        occupancy: row.occupancy !== '' ? parseFloat(row.occupancy) : null,
        adr: row.adr !== '' ? parseFloat(row.adr) : null,
        revpar: row.revpar !== '' ? parseFloat(row.revpar) : null,
      }
    })
  }, [draft])

  useEffect(() => {
    if (!hasDraftChanges) {
      setPreview(null)
      return
    }

    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      setPreviewLoading(true)
      apiFetch(`${base}/targets/preview`, {
        method: 'POST',
        body: JSON.stringify({ months: buildPreviewPayload() }),
      })
        .then((r) => r.json())
        .then((data: PreviewResult) => {
          setPreview(data)
          setPreviewLoading(false)
        })
        .catch(() => setPreviewLoading(false))
    }, 600)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [draft, hasDraftChanges, buildPreviewPayload, base])

  function handleUpdate(month: number, field: 'occupancy' | 'adr', value: string) {
    setDraft((prev) => ({
      ...prev,
      [month]: { ...prev[month], [field]: value, isDirty: true, willClear: false },
    }))
  }

  function handleApplyRemaining(fromMonth: number) {
    const source = draft[fromMonth]
    const later = ALL_MONTHS.filter((m) => m > fromMonth)
    setDraft((prev) => {
      const next = { ...prev }
      later.forEach((m) => {
        next[m] = { ...next[m], occupancy: source.occupancy, adr: source.adr, isDirty: true, willClear: false }
      })
      return next
    })
  }

  function handleClearRow(month: number) {
    if (hasSavedValues(savedByMonth[month])) {
      // Mark as "will clear on save" rather than immediately removing
      setDraft((prev) => ({
        ...prev,
        [month]: { ...prev[month], occupancy: '', adr: '', isDirty: true, willClear: true },
      }))
    } else {
      setDraft((prev) => ({
        ...prev,
        [month]: { ...prev[month], occupancy: '', adr: '', isDirty: false, willClear: false },
      }))
    }
  }

  function handleDiscard() {
    setDraft(buildInitialDraft())
    setPreview(null)
  }

  function handleSave() {
    setSaving(true)
    router.patch(
      `${base}/targets`,
      { months: buildPreviewPayload() },
      {
        onFinish: () => setSaving(false),
        onSuccess: () => {
          setConfirmOpen(false)
          setDraft((prev) => {
            const next = { ...prev }
            for (const m of Object.keys(next)) {
              next[Number(m)] = { ...next[Number(m)], isDirty: false, willClear: false }
            }
            return next
          })
        },
      },
    )
  }

  return (
    <OperatorLayout title="Targets" currentUser={currentUser} property={property} properties={properties}>
      {/* Page header */}
      <div className="animate-slide-in mb-6 flex items-start justify-between gap-4" style={{ animationDelay: '0ms' }}>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground">Targets</h1>
            <HelpTooltip
              content="Set the occupancy you want for a month and the engine steers rates towards it — discounting when you're behind, holding out for more when you're ahead. No target means the month prices on booking history alone."
              learnMore="/guides/targets"
            />
          </div>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Targets repeat every year until you change them.
          </p>
        </div>

        <Button
          onClick={() => setConfirmOpen(true)}
          disabled={!hasDraftChanges || saving}
          className="shrink-0 gap-1.5 rounded-lg"
        >
          <Rocket className="h-3.5 w-3.5" />
          Save &amp; deploy
        </Button>
      </div>

      {/* WHOSE TARGET THIS IS. A group is steered to ONE occupancy target measured over pooled
          capacity, and the rows live on the lead — so at a lead the page says it covers the unit,
          and at a MEMBER it says the target it is showing is the group's, kept at the lead. The
          member's page is not deferred here (it renders the lead's rows, which is what the engine
          actually steers to), so silence would let an operator read a pooled target as this hotel's
          own. Never in the layout banner rail: target comms are a readout, not a fault. */}
      <div className="mb-6 space-y-3">
        <UnitScopeNote
          scope={reportingScope}
          covers="Every target, projection and result below"
          listHotels
          className="animate-slide-in"
        />
        <DeferredScopeNote
          scope={reportingScope}
          pooled="These targets, and the projections against them"
          own="they are the building's one target, set and kept at the lead listing"
          leadSuffix="/targets"
          className="animate-slide-in"
        />
      </div>

      {!anyTargetsSaved && !hasDraftChanges && (
        <div className="mb-6 animate-slide-in" style={{ animationDelay: '50ms' }}>
          <EmptyStateCard />
        </div>
      )}

      {/* Months table — full page width. */}
      <div
        className="animate-slide-in overflow-x-auto rounded-xl border border-border/60 bg-white shadow-sm"
        style={{ animationDelay: '100ms' }}
      >
        <div className="flex items-center justify-between border-b border-border/40 px-5 py-3">
          <p className="text-sm font-semibold text-foreground">Months you’re steering</p>
          <p className="text-xs text-muted-foreground">
            {anyTargetsSaved
              ? `${savedTargets.length} month${savedTargets.length === 1 ? '' : 's'} covered`
              : 'No months covered yet'}
          </p>
        </div>

        <Table>
          <TableHeader>
            <TableRow className="border-b border-border/40 bg-muted/30 hover:bg-muted/30">
              <TableHead className="w-[190px] py-2.5 pl-5 pr-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Month
              </TableHead>
              <TableHead className="w-[150px] px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <span className="flex items-center gap-1">
                  Target
                  <HelpTooltip
                    content="The occupancy you're aiming for this month. This is the switch — a month with no target isn't steered at all."
                    learnMore="/guides/targets#setting-a-target"
                  />
                </span>
              </TableHead>
              <TableHead className="w-[150px] px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <span className="flex items-center gap-1">
                  ADR floor
                  <HelpTooltip
                    content={
                      adrScope
                        ? `The engine won't discount below this rate while chasing the target. Leave it blank and it works out a floor from the ${adrScope.adrPhrase} you've actually been achieving.${adrScope.floorSentence ? ` ${adrScope.floorSentence}` : ''}`
                        : "The engine won't discount below this rate while chasing the target. Leave it blank and it works out a floor from what you've actually been achieving."
                    }
                    learnMore="/guides/targets#adr-floor"
                  />
                </span>
              </TableHead>
              <TableHead className="w-[160px] px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <span className="flex items-center gap-1">
                  Projected end-of-month
                  <HelpTooltip content="Where the month lands if bookings carry on as normal — what's on the books plus the pickup this property usually still gets. The range below is how far either side it realistically falls." />
                </span>
              </TableHead>
              <TableHead className="w-[140px] px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                <span className="flex items-center gap-1">
                  Target gap
                  <HelpTooltip content="How far the projection sits above or below your target, in occupancy points. Read it next to the days left — the same gap means very different things at 30 days and at 4." />
                </span>
              </TableHead>
              <TableHead className="px-3 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                What the engine is doing
              </TableHead>
              <TableHead className="w-[60px] py-2.5 pl-2 pr-4" aria-label="Row actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {ALL_MONTHS.map((month, idx) => (
              <TargetRow
                key={month}
                month={month}
                base={base}
                saved={savedByMonth[month]}
                actual={lastYearActuals[month] ?? null}
                draft={draft[month]}
                currency={property.currency}
                isCurrentMonth={month === currentMonth}
                forecast={forecastByMonth[month]}
                verdict={verdictByMonth[month]}
                forecastContext={editedSet.has(month) ? preview?.forecast_context?.[month] : undefined}
                onUpdate={handleUpdate}
                onApplyRemaining={handleApplyRemaining}
                onClearRow={handleClearRow}
                onApplySuggestion={applySuggestion}
                animDelay={120 + idx * 20}
              />
            ))}
          </TableBody>
        </Table>

      </div>

      {/* The receipts, under the months they describe. Absent until a month has closed — the
          section renders nothing rather than an empty heading. The seed CTA edits the draft in
          place through the same helper the verdict CTAs use, so it lands in the preview bar
          above and still needs Save & deploy. */}
      <TargetResultsSection
        results={results}
        currency={property.currency}
        adrLabel={adrScope?.adrLabel}
        onApplySuggestion={applySuggestion}
        animDelay={140}
      />

      {/* Impact preview — appears only when there is a draft to explain. */}
      {hasDraftChanges && (
        <PreviewBar
          preview={preview}
          loading={previewLoading}
          currency={property.currency}
          editedCount={editedMonths.length}
          saving={saving}
          onDiscard={handleDiscard}
          onSave={() => setConfirmOpen(true)}
        />
      )}

      {/* Confirm deploy dialog */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="sm:max-w-[480px]">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Rocket className="h-4 w-4 text-primary" />
              Save &amp; deploy targets?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm leading-relaxed">
              These targets start steering rates{' '}
              <span className="font-medium text-foreground">within about a minute</span>. The projection refreshes
              straight away.
              {preview && preview.dates_affected > 0 && (
                <span className="mt-2 block rounded-lg bg-muted px-3 py-2 text-xs font-medium text-foreground">
                  Expected impact: {preview.avg_modifier_pct > 0 ? '+' : ''}
                  {preview.avg_modifier_pct}% average rate change on {preview.dates_affected} dates
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSave}
              disabled={saving}
              className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {saving ? 'Saving…' : 'Save & deploy'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </OperatorLayout>
  )
}
